<template>
  <div
    class="filtersModal"
    @touchstart.passive="onPointerDown"
    @touchmove="onPointerMove"
    @touchend="onPointerUp"
  >
    <div
      class="filtersModal__overlay"
      @click="close"
    />
    <div
      ref="modal"
      class="filtersModal__wrapper"
    >
      <div class="filtersModal__header">
        <h2>{{ selectedFilter.title }}</h2>
      </div>
      <div
        ref="content"
        class="filtersModal__content"
      >
        <component :is="selectedFilter.component" />
      </div>
      <div
        class="filtersModal__footer"
      >
        <div class="filtersModal__footer--text">
          <span>{{ priceText }}</span>
          <div class="bold">
            {{ fromPrice }}
          </div>
        </div>
        <div class="flex gap-1">
          <button
            v-if="filtersStore.isFilterActive(propertyKeys)"
            class="abBtn abBtn--whiteBlue"
            @click="reset"
          >
            {{ $l('showresult.filter.resetTitle') }}
          </button>
          <button
            class="abBtn abBtn--blueWhite"
            @click="close"
          >
            {{ $l('global.simplePhrases.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { l, lc, lreplace } from '@js/utils/toolbox';

const props = defineProps({
	selectedFilter: {
		type: Object,
		required: true
	},
	propertyKeys: {
		type: Array,
		required: true
	}
});

import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();

const resultCount = computed(() => resultQueryStore.state.response?.meta?.resultCount);
const cheapest = computed(() => resultQueryStore.state.response?.sortingButtons?.cheapest);

const isValid = computed(() => cheapest.value && resultCount.value);

const priceText = computed(() => {
	if (!isValid.value && resultCount.value !== undefined) {
		return lreplace('showresult.filter.filtered_results', { x: resultCount.value }) + l('showresult.filter.no_results_on_current_filter');
	}

	if (resultCount.value !== undefined) {
		return lreplace('showresult.filter.filtered_results', { x: resultCount.value });
	}

	return '';
});

const fromPrice = computed(() => isValid.value ? lreplace('showresult.filter.price_from', { price: lc(cheapest.value) }) : '');

const emit = defineEmits(['close', 'reset']);

function close () {
	emit('close');
}

function slideDown () {
	modal.value.style.transition = 'transform 0.2s ease-out';
	modal.value.style.transform = `translate3d(0, var(--window-height), 0)`;
	return new Promise(resolve => modal.value.addEventListener('transitionend', resolve, { once: true }));
}

function reset () {
	emit('reset');
}


// Dragging logic
const modal = ref(null);
const content = ref(null);

// Drag constants
const threshold = 120; // px needed to dismiss

// Drag state
let startY = 0;
let latestY = 0;
let frameId = null;

let shouldDrag = false;

function onPointerDown (e) {
	const eventTarget = e.target;
	// Check if the target is the content or a child of the content
	shouldDrag = content.value?.scrollTop === 0 || !eventTarget.closest('.filtersModal__content');
	// User has scrolled the content, so we don't want to drag
	if (!shouldDrag) {
		return;
	}
	startY = e.touches[0].clientY;
	latestY = 0;
}

function onPointerMove (e) {
	// User has scrolled the content, so we don't want to drag
	if (!shouldDrag) {
		return;
	}

	// const diff = e.clientY - startY;
	const currentY = e.touches[0].clientY;
	const diff = currentY - startY;

	if (diff <= 0) {
		return;
	}

	latestY = diff;

	if (frameId === null) {
		frameId = requestAnimationFrame(() => {
			if (modal.value) {
				modal.value.style.transform = `translate3d(0, ${latestY}px, 0)`;
			}
			frameId = null;
		});
	}
}

async function onPointerUp () {
	// always stop the scheduled RAF callback
	if (frameId !== null) {
		cancelAnimationFrame(frameId);
		frameId = null;
	}

	// decide whether to close or snap back
	if (latestY > threshold) {
		await slideDown();
		close();
	} else {
		modal.value.style.transform = 'translate3d(0, 0, 0)';
	}
	// reset state for the next drag
	startY = 0;
	latestY = 0;
	shouldDrag = false;
}

onUnmounted(() => {
	if (frameId !== null) {
		cancelAnimationFrame(frameId);
		frameId = null;
	}
});
</script>


<style scoped lang="scss">
.filtersModal {
  position: fixed;
  inset: 0; // shorthand for top/right/bottom/left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: $z-Alert;
  color: $black;

  @include breakpoint_between(md, lg) {
    align-items: end;
  }
}

.filtersModal__overlay {
  position: absolute;
  inset: 0;
  background-color: $overlayShade;
}

.filtersModal__wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100% - 5rem);
  background: $white;
  border-top-left-radius:  $borderRadius-sm;
  border-top-right-radius: $borderRadius-sm;
  will-change: transform;
  touch-action: none;
  transform: translate3d(0,0,0);

  @include breakpoint_between(md, lg) {
    max-width: 40rem;
  }
}

.filtersModal__header {
  padding: 1.5rem;
  h2 { margin: 0; }
}

.filtersModal__content {
  flex: 1 1 auto;
  overflow-y: auto;
}

.filtersModal__footer {
  flex: 0 0 6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: $white;

  @include breakpoint_between(md,lg) {
    font-size: 1.6rem;
  }
}

.filtersView {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    background-color: $white;
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
}

</style>
